import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import Sidebar from "../Sidebar";
import { Link, useHistory } from "react-router-dom";

import { getLoginDetails, SESSION_PROMPT_APP_DOWNLOAD_AFTER_LOGIN } from "../../utils/storage";

import { ReactComponent as LogoutIcon } from "../../icons/logout.svg";
import CaretIcon from "../../icons/caret.png";

import BG from "../../icons/bg.png";
import AppDownloadModalContent from "../AppDownloadModalContent";

import Modal from "../Modal";
import Button from "../Button";
import FormInput from "../FormInput";

import useIsMobile from "../../hooks/useIsMobile";

import { ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";

let TIMER = null;

/** App download navbar CTA + modal — set `true` when you want this live again. */
const ENABLE_APP_DOWNLOAD = true;

function getInitialAppDownloadModalOpen() {
  if (!ENABLE_APP_DOWNLOAD) return false;
  try {
    const v = sessionStorage.getItem(SESSION_PROMPT_APP_DOWNLOAD_AFTER_LOGIN);
    if (v !== "1" && v !== "open") return false;
    return (getLoginDetails() || {}).hierarchy === 2;
  } catch (e) {
    return false;
  }
}

const creditPolicy = [
  {
    title: "Monthly Reset",
    desc: [
      "Lookup credits are reset at the beginning of every calendar month.",
    ],
  },
  {
    title: "Free Tier",
    desc: ["The first 50 lookups each month are offered free of charge."],
  },
  
  {
    title: "Subscription Requirement",
    desc: [
      "If a user exhausts all available credits, they must subscribe to purchase additional lookup credits.",
    ],
  },
  {
    title: "Credit Expiry Policy",
    desc: [
      "Free credits and credits earned through sales expire at the end of each month.",
      "Purchased credits do not expire and will remain available until fully used.",
    ],
  },
  {
    title: "Real-Time Tracking",
    desc: [
      "Users can view their credit balance and usage history on their subscription dashboard.",
    ],
  },
];

const PageLayout = ({
  children,
  heading,
  icon,
  searchPlaceholder,
  onSearchChange = () => {},
  hideSearchBar,
  hideLoginDetails,
  noSideBar,
  defaultSearchVal,
  svg,
  hideTop = false,
  credits,
  getCredits = () => {},
}) => {
  const isMobile = useIsMobile();
  const history = useHistory();

  const [isDropdownShown, setIsDropdownShown] = useState(false);
  const [searchValue, setSearchValue] = useState(
    defaultSearchVal ? defaultSearchVal : ""
  );
  const [hideSideBar, setHideSideBar] = useState(false);
  const [lookupInfoClicked, setLookupInfoClicked] = useState(false);
  const { firstName, lastName, organisationName, profileUrl, hierarchy } =
    getLoginDetails() || {};
  const showAppDownload = ENABLE_APP_DOWNLOAD && hierarchy === 2;

  const [appDownloadModalOpen, setAppDownloadModalOpen] = useState(
    getInitialAppDownloadModalOpen
  );

  const dismissAppDownloadModal = useCallback(() => {
    setAppDownloadModalOpen(false);
    try {
      sessionStorage.removeItem(SESSION_PROMPT_APP_DOWNLOAD_AFTER_LOGIN);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!ENABLE_APP_DOWNLOAD || !showAppDownload) return;
    try {
      const v = sessionStorage.getItem(SESSION_PROMPT_APP_DOWNLOAD_AFTER_LOGIN);
      if (v !== "1" && v !== "open") return;
      const details = getLoginDetails() || {};
      if (details.hierarchy !== 2) {
        sessionStorage.removeItem(SESSION_PROMPT_APP_DOWNLOAD_AFTER_LOGIN);
        return;
      }
      if (v === "1") {
        sessionStorage.setItem(SESSION_PROMPT_APP_DOWNLOAD_AFTER_LOGIN, "open");
      }
      setAppDownloadModalOpen(true);
    } catch (e) {
      /* ignore */
    }
  }, [showAppDownload]);

  useEffect(() => {
    if (!showAppDownload) {
      setAppDownloadModalOpen(false);
    }
  }, [showAppDownload]);

  const toolbarHeight = isMobile ? 70 : 60;
  const headingNavRef = useRef(null);
  const navbarRightRef = useRef(null);
  const appDownloadWrapRef = useRef(null);
  const [appDownloadCenterPx, setAppDownloadCenterPx] = useState(null);

  const updateAppDownloadPosition = useCallback(() => {
    if (!showAppDownload || isMobile) return;
    const hEl = headingNavRef.current;
    const rEl = navbarRightRef.current;
    const wrapEl = appDownloadWrapRef.current;
    if (!hEl || !rEl) return;
    const h = hEl.getBoundingClientRect();
    const r = rEl.getBoundingClientRect();
    const halfW = wrapEl
      ? wrapEl.getBoundingClientRect().width / 2
      : 72;
    const gapMid = (h.right + r.left) / 2;
    const minC = h.right + halfW + 4;
    const maxC = r.left - halfW - 4;
    let cx = gapMid;
    if (maxC >= minC) {
      cx = Math.min(Math.max(gapMid, minC), maxC);
    } else {
      cx = Math.min(Math.max(gapMid, h.right + halfW + 4), r.left - halfW - 4);
    }
    setAppDownloadCenterPx(cx);
  }, [isMobile, showAppDownload]);

  useLayoutEffect(() => {
    if (!showAppDownload || isMobile) {
      setAppDownloadCenterPx(null);
      return undefined;
    }
    updateAppDownloadPosition();
    window.addEventListener("resize", updateAppDownloadPosition);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateAppDownloadPosition());
      if (headingNavRef.current) ro.observe(headingNavRef.current);
      if (navbarRightRef.current) ro.observe(navbarRightRef.current);
    }
    const id = window.requestAnimationFrame(updateAppDownloadPosition);
    return () => {
      window.removeEventListener("resize", updateAppDownloadPosition);
      window.cancelAnimationFrame(id);
      if (ro) ro.disconnect();
    };
  }, [
    updateAppDownloadPosition,
    heading,
    hideSearchBar,
    hideLoginDetails,
    isMobile,
    hideSideBar,
    noSideBar,
    credits,
    showAppDownload,
  ]);

  const appDownloadButton = showAppDownload ? (
    <Button
      type="modalCancel"
      style={{
        pointerEvents: "auto",
        padding: isMobile ? "4px 6px" : "8px 14px",
        fontSize: isMobile ? 9 : 13,
        whiteSpace: "nowrap",
        flexShrink: 0,
        marginRight: isMobile ? 8 : 0,
      }}
      onClick={() => setAppDownloadModalOpen(true)}
    >
      APP DOWNLOAD
    </Button>
  ) : null;

  const CreditPolicyList = () => {
    return (
      <div>
        {creditPolicy.map((item, index) => (
          <div key={index} style={{ marginBottom: "1rem", fontSize: 16 }}>
            {item.desc.length === 1 ? (
              <div>
                <strong>
                  {index + 1}. {item.title}:
                </strong>{" "}
                {item.desc[0]}
              </div>
            ) : (
              <div>
                <strong>
                  {index + 1}. {item.title}:
                </strong>
                <ul style={{ marginTop: 5 }}>
                  {item.desc.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  return (
    <div
      className={`t-pl-72 ${hideSideBar ? "sidebarHidden" : "d-pl-250 "} ${
        hideTop ? "" : `pt-80`
      } pb-80 ml-20 pr-20 tabs-wrapper `}
    >
      <div
        className={`p-fixed  wt-100vw l-0 t-0 ${
          isMobile ? "ht-70" : "ht-60"
        }  z-1 navbar-wrapper d-flex`}
      >
        <div
          ref={showAppDownload ? headingNavRef : undefined}
          className="p-fixed d-l-250 t-l-75 ml-20 t-8 fs-28 fw-bolder flex flex-middle align-items-center mt-8"
          onClick={() => setHideSideBar(!hideSideBar)}
        >
          {icon && <img alt="" src={icon} className="wt-30 mr-8 t-hide" />}

          <span
            className="navbar-heading"
            style={{
              fontSize: isMobile ? 10 : "auto",
              width: isMobile ? 140 : "auto",
            }}
          >
            {heading}
          </span>
        </div>
        {showAppDownload && !isMobile && (
          <div
            ref={appDownloadWrapRef}
            style={{
              position: "fixed",
              left:
                appDownloadCenterPx != null
                  ? `${appDownloadCenterPx}px`
                  : "50%",
              transform: "translateX(-50%)",
              top: 0,
              height: toolbarHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            {appDownloadButton}
          </div>
        )}
        <div
          ref={showAppDownload ? navbarRightRef : undefined}
          className={` p-absolute ${
            isMobile ? "ht-70" : "ht-60"
          } r-0 t-0 flex flex-middle pr-16 navbar-right`}
        >
          {showAppDownload && isMobile && appDownloadButton}
          {!hideSearchBar && (
            <FormInput
              className="top_form_input mr-32"
              inputType="search"
              value={searchValue}
              onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);
                if (TIMER) {
                  clearTimeout(TIMER);
                }
                TIMER = setTimeout(() => {
                  onSearchChange(val);
                }, 500);
              }}
              placeholder={searchPlaceholder}
            />
          )}
          {!hideLoginDetails && (
            <div
              className="flex flex-middle pl-4 pr-4"
              onClick={() => {
                setIsDropdownShown((dropdown) => true);
              }}
            >
              {!isMobile && credits != null && hierarchy === 2 && (
                <div style={{ display: "flex", marginRight: 24 }}>
                  <div
                    style={{
                      marginRight: "16px",
                      fontSize: 18,
                      fontWeight: "bolder",
                      color: "red",
                    }}
                  >
                    Lookup Credit : {credits > 250000 ? 'UNLIMITED' : credits}
                    <sup style={{ marginLeft: 4, cursor: "pointer" }}>
                      <InfoCircleOutlined
                        onClick={() => {
                          setLookupInfoClicked(true);
                        }}
                      />
                    </sup>
                  </div>
                  <div>
                    <ReloadOutlined
                      onClick={() => {
                        getCredits();
                      }}
                      style={{
                        fontSize: 18,
                        fontWeight: "bolder",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                </div>
              )}

              <div
                className="rounded-circle"
                style={{
                  overflow: "hidden",
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              >
                <img
                  className="w-100"
                  style={{ height: "100%" }}
                  src={
                    profileUrl ||
                    `https://s3.ap-south-1.amazonaws.com/cat-general-bucket/c4ee9b8e-129c-402f-acd1-779caf9342f51591591440282.jpeg`
                  }
                  alt=""
                />
              </div>

              <div className="dropdown admin-dropdown">
                <div
                  id="dropdownMenuLink"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  className="mr-12 rounded flex items-center"
                >
                  {firstName} {lastName}
                  <img
                    src={CaretIcon}
                    style={{ height: "intrinsic" }}
                    className="wt-20  op-5 mr-4 c-pointer"
                  />
                </div>

                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuLink"
                >
                  <li>
                    <a className="dropdown-item">
                      <Link to="/settings">
                        <img
                          className="wt-20 mr-12 rotate--90"
                          src={CaretIcon}
                        />
                        <span>User Profile</span>
                      </Link>
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item">
                      <Link to="/logout">
                        <img
                          className="wt-20 mr-12 rotate--90 decoration-none"
                          src={CaretIcon}
                        />
                        <span>Logout</span>
                      </Link>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      {!hideLoginDetails && isMobile && credits != null && hierarchy === 2 && (
        <div
          style={{ display: "flex", marginRight: 24, justifySelf: "center" }}
        >
          <div
            style={{
              marginRight: "16px",
              fontSize: 18,
              fontWeight: "bolder",
              color: "red",
            }}
          >
            Lookup Credit : {credits > 250000 ? 'UNLIMITED' : credits}
            {/* <sup style={{ marginLeft: 4, cursor: "pointer" }}>
              <InfoCircleOutlined
                onClick={() => {
                  setLookupInfoClicked(true);
                }}
              />
            </sup> */}
          </div>
          <div>
            <ReloadOutlined
              onClick={() => {
                getCredits();
              }}
              style={{
                fontSize: 18,
                fontWeight: "bolder",
                cursor: "pointer",
              }}
            />
          </div>
        </div>
      )}

      {/* {isDropdownShown && (
        <OutsideClickHandler onOutsideClick={() => setIsDropdownShown(false)}>
          <div
            className=" bg-white wt-130 p-fixed box-shadow pt-8 pb-4 pl-8 pr-8"
            style={{ top: 50, right: 20, zIndex: 20 }}
          >
            <Link
              to="/settings"
              className="decoration-none flex flex-middle c-gray fw-14 mb-4"
            >
              <img
                src={CaretIcon}
                className="wt-20 rotate--90 op-5 mr-4 c-pointer"
              />
              <span>USER PROFILE</span>
            </Link>
            <Link
              to="/logout"
              className="decoration-none flex flex-middle c-gray fw-14 mb-4"
            >
              <img
                src={CaretIcon}
                className="wt-20 rotate--90 op-5 mr-4 c-pointer"
              />
              <span>LOGOUT</span>
            </Link>
          </div>
        </OutsideClickHandler>
      )} */}

      {showAppDownload && (
        <Modal
          isOpen={appDownloadModalOpen}
          onClose={dismissAppDownloadModal}
          maxWidth="min(98vw, 980px)"
        >
          <AppDownloadModalContent />
        </Modal>
      )}

      {lookupInfoClicked && (
        <Modal>
          <div className="wt-700 mt-8">
            <div className="fs-24 fw-bolder mb-40">
              Lookup Credit Policy (Subscription Model)
            </div>
            <div className="flex flex-left mb-40 flex-wrap">
              {CreditPolicyList()}
            </div>
            <div className="flex flex-middle flex-right">
              <Button
                className="wt-100 mr-20"
                type="secondary"
                onClick={() => {
                  setLookupInfoClicked(false);
                }}
              >
                close
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {!hideSideBar && !noSideBar && <Sidebar />}
      {children}
    </div>
  );
};

export default PageLayout;
